import { DOMHelper } from "../../Helpers/DOMHelper.js";
import { FormatHelper } from "../../Helpers/FormatHelper.js";
import { InputSlider } from "../component/InputSlider.js";

const DOWN_PAYMENT_SLIDER_ID = "calc-down-payment-slider";
const DEFAULT_APR = 6.9;
//PmRates generic quotes carry no disclaimer (it's just math, not a real lender) — same wording as GridWidget's getDefaultDisclaimer()
const GENERIC_RATE_DISCLAIMER = "This is an estimate only, not an offer of credit. Actual payments exclude applicable taxes, title, registration, license, and documentary fees, and will vary depending upon the final price and terms agreed upon between you and your dealer.";

export class CalculatorWidget {
    constructor(app, vehicle) {
        this.app = app;
        this.vehicle = vehicle;
        this.containerSelector = ".pmdrs-calculator-widget";
        this.availableRatesOn = false;
        // CalculatorWidget is still a demo, not an official widget — hold off on reading a
        // per-site SiteSettings value until we actually commit to storing settings for it.
        // Hardcoded default for now.
        // this.customApr = parseFloat(this.app.siteSettings.calculatorWidgetDefaultApr) || DEFAULT_APR;
        this.customApr = DEFAULT_APR;
    }

    init() {
        this.addHtml();
        this.populateTermSelect();
        this.populateCreditScoreSelect();

        // CalculatorWidget is still a demo, not an official widget — hold off on reading a
        // per-site SiteSettings value until we actually commit to storing settings for it.
        // Hardcoded temp limit for now.
        // let maxCashDownPercent = parseFloat(this.app.siteSettings.maxCashDownPercent) || 0.6;
        let maxCashDownPercent = 0.6;
        let maxCashDown = this.vehicle.finalPrice * maxCashDownPercent;
        this.downPaymentSlider = new InputSlider(this.app, DOWN_PAYMENT_SLIDER_ID, "calcDownPayment", 0, maxCashDown, true);
        this.downPaymentSlider.init();

        this.addHandlers();
        this.recalculate();
    }

    //no-op: CalculatorWidget is self-contained, not a click-to-open-modal launcher like TabsWidget/GridWidget/CTAButton
    setBtnClickCallback(callbackFunction) {
        this.btnClickCallback = callbackFunction;
    }

    addHtml() {
        let template = `<div class="pmdrs-widget pmdrs-calculator-widget">
    <span class="calc-title">Estimate Your Payment</span>
    <div class="calc-result">
        <span class="calc-result-label">Your estimated monthly payment</span>
        <div class="calc-result-value">
            <span class="calc-monthly-payment">--</span><span class="calc-result-suffix">/mo</span>
        </div>
        <div class="calc-result-meta">
            <span class="calc-lender-name"></span>
            <div class="drs-tooltip calc-disclaimer-tooltip">
                <span>Disclaimer</span> <i class="pmdrs-fa pmdrs-fa-info-circle"></i>
                <span class="drs-tooltiptext-large calc-disclaimer-text" style="font-size: 13px;"></span>
            </div>
        </div>
        <div class="calc-result-details">
            <div class="calc-detail-row">
                <span class="calc-detail-key">Down Payment</span>
                <span class="calc-detail-downpayment">--</span>
            </div>
            <div class="calc-detail-row">
                <span class="calc-detail-key">Amount Financed</span>
                <span class="calc-detail-financed">--</span>
            </div>
        </div>
    </div>
    <div class="calc-inputs">
        <div class="contact-form-group calc-field">
            <label class="contact-form-label calc-label">Down Payment</label>
            <div id="calc-down-payment-slider"></div>
        </div>
        <div class="contact-form-group calc-field">
            <label class="contact-form-label calc-label">For how long?</label>
            <select class="contact-form-input calc-term-select"></select>
        </div>
        <div class="contact-form-group calc-field">
            <label class="contact-form-label calc-label">Credit Rating</label>
            <select class="contact-form-input calc-credit-score-select"></select>
        </div>
        <div class="contact-form-group calc-field calc-apr-field">
            <label class="contact-form-label calc-label">Interest rate</label>
            <div class="calc-apr-row">
                <div class="calc-apr-input-wrap">
                    <input class="contact-form-input calc-apr-input" type="number" step="0.01" min="0" max="29.99" value="{{apr}}" />
                    <span class="calc-apr-suffix">%</span>
                </div>
                <label class="calc-available-rates-label">
                    <input type="checkbox" class="calc-available-rates-checkbox" />
                    <span>Available Rates</span>
                </label>
            </div>
        </div>
    </div>
</div>
`;
        template = template.replaceAll("{{apr}}", this.customApr);

        document.querySelector(this.app.getSelector("")).insertAdjacentHTML("beforeend", template);
    }

    getSelector(selector) {
        return this.app.getSelector(this.containerSelector + " " + selector);
    }

    getContainerElement() {
        return document.querySelector(this.app.getSelector(this.containerSelector));
    }

    populateTermSelect() {
        let select = this.getContainerElement().querySelector(".calc-term-select");
        let ratingLabel = this.app.getDefaultCreditRatingLabel("finance");
        let termOptions = this.app.getTermsOptions("finance", ratingLabel, null);

        let optionsHtml = "";
        termOptions.forEach((term) => {
            optionsHtml += "<option value='" + term + "'>" + this.app.loanTermDisplay(term, null) + "</option>";
        });
        select.innerHTML = optionsHtml;
        //default to the longest term available, matching the reference calculator's default
        if (termOptions.length) {
            select.value = termOptions[termOptions.length - 1];
        }
    }

    populateCreditScoreSelect() {
        let select = this.getContainerElement().querySelector(".calc-credit-score-select");
        let rangeConfig = this.app.getCreditRangeConfig("finance");

        let optionsHtml = "";
        rangeConfig.forEach((config) => {
            optionsHtml +=
                "<option value='" + config.label + "' data-min=" + config.min + " data-max=" + config.max + ">" +
                FormatHelper.capitalizeFirstLetter(config.label).replaceAll("_", " ") +
                " (" + config.min + " - " + config.max + ")" +
                "</option>";
        });
        select.innerHTML = optionsHtml;
    }

    getSelectedTerm() {
        return parseInt(this.getContainerElement().querySelector(".calc-term-select").value, 10);
    }

    getSelectedCreditScore() {
        let select = this.getContainerElement().querySelector(".calc-credit-score-select");
        let selectedOption = select.options[select.selectedIndex];
        return selectedOption ? parseInt(selectedOption.dataset.max, 10) : 0;
    }

    getDownPayment() {
        return this.downPaymentSlider ? this.downPaymentSlider.getValue() : 0;
    }

    isAvailableRatesOn() {
        return this.availableRatesOn;
    }

    addHandlers() {
        let container = this.getContainerElement();
        let $this = this;

        DOMHelper.addHandler(this.getSelector(".calc-term-select"), "change", () => $this.recalculate());
        DOMHelper.addHandler(this.getSelector(".calc-credit-score-select"), "change", () => $this.recalculate());
        this.downPaymentSlider.setOnChange(() => $this.recalculate());

        DOMHelper.addHandler(this.getSelector(".calc-apr-input"), "change", (event) => {
            $this.customApr = parseFloat(event.target.value) || 0;
            $this.recalculate();
        });

        DOMHelper.addHandler(this.getSelector(".calc-available-rates-checkbox"), "change", (event) => {
            $this.availableRatesOn = event.target.checked;
            $this.setAprInputDisabled($this.availableRatesOn);
            $this.recalculate();
        });
    }

    setAprInputDisabled(disabled) {
        let aprInput = this.getContainerElement().querySelector(".calc-apr-input");
        aprInput.disabled = disabled;
    }

    setAprDisplay(aprPercent) {
        this.getContainerElement().querySelector(".calc-apr-input").value = parseFloat(aprPercent).toFixed(2);
    }

    setMonthlyPaymentDisplay(monthlyPayment, downPayment, financedAmount, disclaimerText, lenderName) {
        let container = this.getContainerElement();
        container.querySelector(".calc-monthly-payment").textContent = FormatHelper.currencyFormat(monthlyPayment || 0, 0);
        container.querySelector(".calc-detail-downpayment").textContent = FormatHelper.currencyFormat(downPayment || 0, 0);
        container.querySelector(".calc-detail-financed").textContent = FormatHelper.currencyFormat(financedAmount || 0, 0);
        DOMHelper.setInnerHTML(this.getSelector(".calc-disclaimer-text"), disclaimerText || GENERIC_RATE_DISCLAIMER);
        //lender name only makes sense for a real Available Rates quote — empty string hides the line via CSS :empty
        container.querySelector(".calc-lender-name").textContent = lenderName ? `Rate provided by ${lenderName}` : "";
    }

    recalculate() {
        let term = this.getSelectedTerm();
        let downPayment = this.getDownPayment();
        let financedAmount = Math.max(0, this.vehicle.finalPrice - downPayment);

        if (this.availableRatesOn) {
            this.recalculateFromDriver(term, downPayment, financedAmount);
        } else {
            this.recalculateFromCustomRate(term, downPayment, financedAmount);
        }
    }

    //"Available Rates" off — custom/fake rate, freely editable APR, hits the generic PmRates endpoint
    recalculateFromCustomRate(term, downPayment, financedAmount) {
        let $this = this;
        let requestParams = {
            Price: this.vehicle.finalPrice,
            Term: term,
            APR: this.customApr,
            DownPayment: downPayment,
            Vin: this.vehicle.vin,
        };

        this.app.pmdrs.pmApi.getPmRatesGenericQuote(
            requestParams,
            (response) => {
                let payments = JSON.parse(response).payments;
                let loanPayment = payments.find((p) => p.offerType === "Loan");
                if (!loanPayment) return;
                //no real lender behind this quote — it's pure math off the customer-entered APR
                $this.setMonthlyPaymentDisplay(loanPayment.payment, downPayment, financedAmount, GENERIC_RATE_DISCLAIMER, null);
            },
            (error) => {
                console.error("Error getting PmRates generic quote", error);
            }
        );
    }

    //"Available Rates" on — reuses the exact same driver strategy every other widget already uses
    //(LenderDeskDriver for automotive/FCA dealers, PixelmotionDriver for motorsports), no new driver code
    recalculateFromDriver(term, downPayment, financedAmount) {
        let $this = this;
        let paymentData = {
            cashDown: downPayment,
            creditRating: this.getSelectedCreditScore(),
            loanMonths: term,
            tradeInData: { tradeInEstimate: 0 },
            mileageAllowance: 0,
            accessoriesPrice: 0,
            conditionalOffersTotal: 0,
            totalIncentives: 0,
            lenderId: null,
            balloonType: "",
            taxesByZipCode: null,
            customerZipCode: null,
        };

        this.app.getPayment(
            false,
            "finance",
            paymentData,
            (paymentObject) => {
                if (!paymentObject) return;
                $this.setAprDisplay(paymentObject.moneyFactor);
                $this.setMonthlyPaymentDisplay(
                    paymentObject.monthlyPayment,
                    downPayment,
                    financedAmount,
                    paymentObject.disclaimerText,
                    paymentObject.lenderName
                );
            },
            (error) => {
                console.error("Error getting driver payment for Available Rates", error);
            }
        );
    }
}
